// Tablas de precios que se leen de la API, NO escritas a mano.
//
// Es la regla más importante de esta documentación. En agosto de 2026 los precios vivían a la vez
// en el código y en la base de datos; discreparon en silencio durante meses y se estuvo cobrando
// la mitad de lo previsto sin que nadie se enterara. Escribir "Starter: 4.200 créditos" en una
// página de ayuda crearía ese mismo problema por tercera vez, y de cara al cliente. Aquí las
// cifras salen de /public/pricing, así que no pueden quedarse viejas: la documentación explica
// CÓMO funciona el sistema y los NÚMEROS los pone el sistema.
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "../lib/api";
import { Table } from "./ui";

let _cache = null;   // una sola petición por sesión: estas tablas salen en varias páginas

export function usePricing() {
  const [data, setData] = useState(_cache);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (_cache) return;
    api.get("/public/pricing")
      .then(({ data }) => { _cache = data; setData(data); })
      .catch(() => setError(true));
  }, []);
  return { data, error };
}

const eur = (n) => `${Number(n).toLocaleString("es-ES")} €`;
const num = (n) => Number(n).toLocaleString("es-ES");

function Cargando({ error }) {
  if (error) {
    return (
      <p className="mb-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
        No se han podido cargar los precios en vivo. Consúltalos en la página de planes.
      </p>
    );
  }
  return (
    <div className="mb-6 flex items-center gap-2 text-sm text-slate-400">
      <Loader2 size={16} className="animate-spin" /> Cargando precios…
    </div>
  );
}

export function TablaPlanes() {
  const { data, error } = usePricing();
  if (!data) return <Cargando error={error} />;
  const ref = data.duracion_referencia_min;
  return (
    <>
      <Table
        head={["Plan", "Precio", "Créditos", `Vídeos de ${ref} min`, "Máx/vídeo", "Canales"]}
        rows={data.planes.map((p) => [
          p.label,
          p.precio_eur ? `${eur(p.precio_eur)}/mes` : "Gratis",
          num(p.creditos),
          Math.round(p.videos_referencia),
          `${p.max_minutos} min`,
          p.canales,
        ])}
      />
      <p className="-mt-2 mb-6 text-[13px] leading-6 text-slate-400">
        Cifras en vivo. La columna de vídeos cuenta todos los planes con la misma duración
        ({ref} minutos) para que se puedan comparar; los que topan por debajo usan su tope.
        «Máx/vídeo» es un límite de duración, no la duración habitual: un vídeo más largo
        consume más créditos y por tanto salen menos.
      </p>
    </>
  );
}

export function TablaRecargas() {
  const { data, error } = usePricing();
  if (!data) return <Cargando error={error} />;
  return (
    <>
      <Table
        head={["Pack", "Precio", "€ por crédito"]}
        rows={data.recargas.map((r) => [
          `${num(r.creditos)} créditos`,
          eur(r.precio_eur),
          (r.precio_eur / r.creditos).toFixed(4).replace(".", ","),
        ])}
      />
      <p className="-mt-2 mb-6 text-[13px] leading-6 text-slate-400">
        Cifras en vivo. Las recargas son un pago único y no caducan mientras tengas plan activo.
      </p>
    </>
  );
}

// "Un vídeo de N minutos consume ~X créditos" — calculado, no escrito a mano.
export function CosteVideo({ minutos = 15 }) {
  const { data } = usePricing();
  if (!data) return <span className="text-slate-400">…</span>;
  const cr = Math.round(minutos * 60 * data.creditos_por_segundo);
  return <span className="font-semibold text-slate-900">{num(cr)} créditos</span>;
}
